import { LoaderCircleIcon } from 'lucide-react';

import { MembershipDTO } from '@invenco/accounts-interface';

import { Skeleton } from '@/components/core/skeleton';
import { cn } from '@/shared/helpers';

import { getMembershipIcon, getMembershipName } from './membership-helpers';

type Props = {
  /**
   * Indicates that a membership is currently being fetched over the network.
   *
   * Note - optimistic updates: it is possible to be in a loading state and already have a membership
   * if the user is currently switching accounts. Having a loading prop and the membership prop
   * allows to optimistically showing the switched account while also showing a loading indicator
   * while the switched account is still being fetched
   */
  loading?: boolean;

  /** The membership to display. */
  membership?: MembershipDTO;

  /** Optional CSS class name. */
  className?: string;
};

export function ActiveMembershipDetail({ loading, membership, className }: Props) {
  const name = getMembershipName(membership);
  return (
    // Other sidebar icons are size-4, this one is size-5. To compensate we use -ml-0.5 and gap-1.5
    // (instead of gap-2) to align the inner icon and keep the text aligned.
    <div className={cn('grid w-full flex-1 grid-cols-[auto_1fr] items-center gap-1.5', className)}>
      <div
        className="-ml-0.5 flex aspect-square size-5 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground [&>svg]:size-3"
        aria-hidden
      >
        {loading ? <LoaderCircleIcon className="animate-spin" /> : getMembershipIcon(membership)}
      </div>
      <div
        className={cn('flex-1 truncate text-sm font-semibold', loading && 'text-muted-foreground')}
      >
        {loading && !name ? <Skeleton className="h-4 w-full" /> : name}
      </div>
    </div>
  );
}
